// =============================================================================
// MapsStatsCard — liczniki per mapa (seeds/nodes/encje/relacje/gotowe/opublikowane)
// =============================================================================

import { Link } from "react-router-dom";
import { Map, CheckCircle2, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useDashboardStats, type MapStatsRow } from "@/hooks/useDashboardStats";

export function MapsStatsCard() {
  const { maps, loading } = useDashboardStats();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Map className="h-4 w-4 text-blue-400" />
          Statystyki map
        </CardTitle>
        <CardDescription>
          Ilosci danych wygenerowanych dla kazdej mapy — seedy, nody, encje, relacje, tresci.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && maps.length === 0 && (
          <p className="text-sm text-muted-foreground italic">Ladowanie...</p>
        )}
        {!loading && maps.length === 0 && (
          <p className="text-sm text-muted-foreground italic">Brak map. Utworz pierwsza w /mapy-tematyczne.</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/30">
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Mapa / domena</th>
                <th className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Seedy</th>
                <th className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nody</th>
                <th className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Encje</th>
                <th className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Relacje</th>
                <th className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Gotowe</th>
                <th className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Opublik.</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Audyt</th>
              </tr>
            </thead>
            <tbody>
              {maps.map((m) => (
                <MapRow key={m.mapId} m={m} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function MapRow({ m }: { m: MapStatsRow }) {
  const auditColor = m.auditScore === null ? "text-muted-foreground/40"
    : m.auditScore >= 80 ? "text-emerald-500"
      : m.auditScore >= 60 ? "text-amber-500"
        : "text-red-400";

  return (
    <tr className="border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors">
      <td className="px-3 py-2 align-middle">
        <Link to={`/mapy-tematyczne/${m.mapId}`} className="flex flex-col min-w-0 hover:text-primary">
          <span className="font-medium truncate max-w-[260px]">{m.mapName}</span>
          <span className="text-[10px] text-muted-foreground truncate max-w-[260px]">
            {m.websiteName ? m.websiteName : "bez domeny"}
            <span className="mx-1">·</span>
            <span className="uppercase text-[9px] tracking-wider">{m.workspace}</span>
          </span>
        </Link>
      </td>
      <td className="px-2 py-2 text-right font-mono tabular-nums text-foreground/80">
        {m.seeds || <span className="text-muted-foreground/40">—</span>}
      </td>
      <td className="px-2 py-2 text-right font-mono tabular-nums text-foreground/80">
        {m.nodes || <span className="text-muted-foreground/40">—</span>}
      </td>
      <td className="px-2 py-2 text-right font-mono tabular-nums text-foreground/80">
        {m.entities || <span className="text-muted-foreground/40">—</span>}
      </td>
      <td className="px-2 py-2 text-right font-mono tabular-nums text-foreground/80">
        {m.relations || <span className="text-muted-foreground/40">—</span>}
      </td>
      <td className="px-2 py-2 text-right font-mono tabular-nums">
        {m.contentReady > 0 ? (
          <span className="inline-flex items-center gap-1 text-blue-400 font-semibold">
            <Send className="h-3 w-3" />{m.contentReady}
          </span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>
      <td className="px-2 py-2 text-right font-mono tabular-nums">
        {m.contentPublished > 0 ? (
          <span className="inline-flex items-center gap-1 text-emerald-500 font-semibold">
            <CheckCircle2 className="h-3 w-3" />{m.contentPublished}
          </span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>
      <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${auditColor}`}>
        {m.auditScore !== null ? m.auditScore : "—"}
      </td>
    </tr>
  );
}
